import React, { useState, useEffect } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import { Form, Table, Card } from 'react-bootstrap';
import Layout from "../../utils/layout";
import useStore from "../../hooks/useStore";
import { observer } from "mobx-react";

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);
const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const ScorecardExcelCompany3 = () => {
  const {
    companyStore: { companyUnit },
    userStore: { user, token },
  } = useStore();

  const [frequency, setFrequency] = useState("Yearly");
  const [year, setYear] = useState(currentYear.toString());
  const [month, setMonth] = useState("January");
  const [scorecardData, setScorecardData] = useState({ water: [], waste: [], emission: [], energy: [] });
  const [targetValues, setTargetValues] = useState({});
  const [groupedTargetValues, setGroupedTargetValues] = useState({ water: {}, waste: {} });
  const [statusSelections, setStatusSelections] = useState({});
  const [loading, setLoading] = useState(false);

 const getBaseYearValue = (field, baseYearData, baseYear) => {
  const group = field.group;
  const dataset = group === "A"
    ? baseYearData.water
    : ["B1", "B2", "B3"].includes(group)
    ? baseYearData.waste
    : [...baseYearData.water, ...baseYearData.waste, ...baseYearData.emission, ...baseYearData.energy];

  if (!dataset || dataset.length === 0) return null;

  const entries = dataset.filter(
    (entry) => String(entry.fieldId) === String(field.fieldId) && String(entry.frequencyYear) === String(baseYear)
  );

  if (entries.length === 0) {
    console.warn(`⚠️ No entries found for fieldId ${field.fieldId} in base year ${baseYear}`);
    return null;
  }

  const baseValue = entries.reduce((sum, entry) => sum + Number(entry.prevValue || 0), 0);
  console.log(`✅ Base value for fieldId ${field.fieldId} in ${baseYear}:`, baseValue);

  return baseValue;
};


  const fetchAllScorecardData = async (fieldIds, companyUnit, userId, selectedYear) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}scorecard/fetch-all`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ fieldIds, companyUnit, userId, selectedYear }),
      });

      const result = await response.json();

      if (result.data && typeof result.data === 'object') {
        const newScorecardData = result.data;
        const groupedData = { water: {}, waste: {} };

        newScorecardData.water.forEach(item => {
          if (item.fieldGroup === "A") {
            const key = `${item.frequencyMonth}-${item.frequencyYear}`;
            groupedData.water[key] = (groupedData.water[key] || 0) + (item.prevValue || 0);
          }
        });

        newScorecardData.waste.forEach(item => {
          if (["B1", "B2", "B3"].includes(item.fieldGroup)) {
            const key = `${item.frequencyMonth}-${item.frequencyYear}`;
            groupedData.waste[key] = (groupedData.waste[key] || 0) + (item.prevValue || 0);
          }
        });

        newScorecardData.groupedWater = groupedData.water;
        newScorecardData.groupedWaste = groupedData.waste;

        return newScorecardData;
      }

      return { water: [], waste: [], emission: [], energy: [], groupedWater: {}, groupedWaste: {} };
    } catch (error) {
      console.error("Error fetching scorecard data:", error);
      return { water: [], waste: [], emission: [], energy: [], groupedWater: {}, groupedWaste: {} };
    }
  };

useEffect(() => {
  const fetchData = async () => {
    if (!user?.id || !companyUnit) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // ✅ Clear previous data
      setScorecardData({ water: [], waste: [], emission: [], energy: [] });
      setTargetValues({});
      setGroupedTargetValues({ water: {}, waste: {} });
      setStatusSelections({});

      const assignedFieldsRes = await fetch(
        `${process.env.REACT_APP_BASE_URL}assign-companies/assignFields/scorecard/${user.id}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await assignedFieldsRes.json();
      if (!result.status || !Array.isArray(result.data)) {
        setLoading(false);
        return;
      }

      const assignedFields = result.data;
      const baseYear = String(Number(year) - 1);
      const fieldIds = assignedFields.map(f => String(f.fieldId));

      // ✅ Fetch base year data
      const baseYearData = await fetchAllScorecardData(fieldIds, companyUnit, user.id, baseYear);
      console.log("Base Year Data:", baseYearData);

      const targets = {};
      const groupedTargets = { water: {}, waste: {} };
      const fieldStatusMapping = {};
      let groupedWaterTotal = 0;
      let groupedWasteTotal = 0;

      for (const field of assignedFields) {
        const fieldId = String(field.fieldId);
        const targetType = field.targetType?.toLowerCase();
        const rawTarget = Number(field.target);

        // ✅ Fetch base year value
        const baseValue = getBaseYearValue(field, baseYearData, baseYear);

        let finalTarget = rawTarget;

        if (!isNaN(baseValue) && (targetType === "reduction" || targetType === "increase")) {
          const ratio = rawTarget / 100;
          finalTarget =
            targetType === "reduction"
              ? +(baseValue * (1 - ratio)).toFixed(2)
              : +(baseValue * (1 + ratio)).toFixed(2);
        }

        console.log(`BaseValue for fieldId ${fieldId}:`, baseValue);
        console.log(`Final Target for fieldId ${fieldId}:`, finalTarget);

        targets[fieldId] = finalTarget;
        fieldStatusMapping[fieldId] = field.scorecardStatus;

        if (field.group === "A" && !isNaN(baseValue)) {
          groupedWaterTotal += baseValue;
        }

        if (["B1", "B2", "B3"].includes(field.group) && !isNaN(baseValue)) {
          groupedWasteTotal += baseValue;
        }
      }

      // ✅ Apply 2% adjustment for grouped values
      groupedTargets.water = { total: +(groupedWaterTotal * 0.98).toFixed(2) }; // 2% reduction
      groupedTargets.waste = { total: +(groupedWasteTotal * 1.02).toFixed(2) }; // 2% increase

      // ✅ Save processed data
      setTargetValues(targets);
      setGroupedTargetValues(groupedTargets);
      setStatusSelections(fieldStatusMapping);

      // ✅ Fetch current year scorecard data
      const currentYearData = await fetchAllScorecardData(fieldIds, companyUnit, user.id, year);
    
      setScorecardData(currentYearData);
    } catch (error) {
      console.error("❌ Error in fetchData:", error);
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [user?.id, companyUnit, year]);

  const calculateGroupedWaterTarget = () => {
  const total = Object.values(groupedTargetValues.water).reduce((sum, val) => sum + (val || 0), 0);
  return frequency === "Monthly" ? (total / 12).toFixed(2) : total.toFixed(2);
};

const calculateGroupedWasteTarget = () => {
  const total = Object.values(groupedTargetValues.waste).reduce((sum, val) => sum + (val || 0), 0);
  return frequency === "Monthly" ? (total / 12).toFixed(2) : total.toFixed(2);
};


const themeCounts = {
  Environment: { total: 0, reached: 0 },
  Governance: { total: 0, reached: 0 },
  Social: { total: 0, reached: 0 },
};



  return (
    <Layout>
      <Card className="p-4">
        <Card.Body>
          <div className="d-flex flex-wrap gap-3 mb-4">
            <Form.Select value={frequency} onChange={(e) => setFrequency(e.target.value)} style={{ width: '200px' }}>
              <option value="Yearly">Yearly</option>
              <option value="Monthly">Monthly</option>
            </Form.Select>

            <Form.Select value={year} onChange={(e) => setYear(e.target.value)} style={{ width: '200px' }}>
              {years.map(yr => <option key={yr} value={yr}>{yr}</option>)}
            </Form.Select>

            {frequency === "Monthly" && (
              <Form.Select value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: '200px' }}>
                <option value="All">All Months</option>
                {months.map(mo => <option key={mo} value={mo}>{mo}</option>)}
              </Form.Select>
            )}
          </div>

          <h4 className="mt-4">Scorecard Data (Company ID 3)</h4>
            <div style={{ maxHeight: '500px', overflowY: 'auto', position: 'relative' }}>
                 <Table>
                  <thead 
                  
                  style={{ 
                    position: 'sticky', 
                    top: 0, 
                    backgroundColor: '#f8f9fa', 
                    zIndex: 10,
                    width : '100%'
                  }}>
                  
                <tr>
                  <th style={{ 
                    position: 'sticky', 
                    top: 0, 
                    backgroundColor: '#f8f9fa', 
                    boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
                  }}>S. No</th>
                  <th style={{ 
                    position: 'sticky', 
                    top: 0, 
                    backgroundColor: '#f8f9fa', 
                    boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
                  }}>Theme</th>
                  <th style={{ 
                    position: 'sticky', 
                    top: 0, 
                    backgroundColor: '#f8f9fa', 
                    boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
                  }}>Description of Target</th>
                  <th style={{ 
                    position: 'sticky', 
                    top: 0, 
                    backgroundColor: '#f8f9fa', 
                    boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
                  }}>Target Type</th>
                  <th style={{ 
                    position: 'sticky', 
                    top: 0, 
                    backgroundColor: '#f8f9fa', 
                    boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
                  }}>UOM</th>
                  <th style={{ 
                    position: 'sticky', 
                    top: 0, 
                    backgroundColor: '#f8f9fa', 
                    boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
                  }}>Frequency Type</th>
                  <th style={{ 
                    position: 'sticky', 
                    top: 0, 
                    backgroundColor: '#f8f9fa', 
                    boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
                  }}>Frequency Month</th>
                  <th style={{ 
                    position: 'sticky', 
                    top: 0, 
                    backgroundColor: '#f8f9fa', 
                    boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
                  }}>Frequency Year</th>
                  <th style={{ 
                    position: 'sticky', 
                    top: 0, 
                    backgroundColor: '#f8f9fa', 
                    boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
                  }}>Target Value</th>
                  <th style={{ 
                    position: 'sticky', 
                    top: 0, 
                    backgroundColor: '#f8f9fa', 
                    boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
                  }}>Current Value</th>
                  {/* <th style={{ 
                    position: 'sticky', 
                    top: 0, 
                    backgroundColor: '#f8f9fa', 
                    boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
                  }}>Status</th>
                  <th style={{ 
                    position: 'sticky', 
                    top: 0, 
                    backgroundColor: '#f8f9fa', 
                    boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
                  }}>Target Reached</th> */}
                  <th style={{ 
                    position: 'sticky', 
                    top: 0, 
                    backgroundColor: '#f8f9fa', 
                    boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
                  }}>Percentage</th>
                </tr>
          
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="13">Loading data...</td>
                </tr>
              ) : (
                <>
                  {(() => {
                    let srNo = 1;
                    const rows = [];
          
          if (scorecardData.groupedWater) {
            if (frequency === "Yearly") {
              // const yearlyWaterTotal = Object.entries(scorecardData.groupedWater)
              //   .filter(([key]) => key.endsWith(year))
              //   .reduce((sum, [, value]) => sum + Number(value || 0), 0);
          
              const yearlyWaterTotal = Object.entries(scorecardData.groupedWater)
                .filter(([key]) => key.endsWith(year))
                .reduce((sum, [, value]) => sum + Number(value || 0), 0)
          
                if (yearlyWaterTotal !== 0) {
              const targetValue = parseFloat(calculateGroupedWaterTarget());
              const selectedStatus = "Lagging"; // Always static
              const targetReached = yearlyWaterTotal <= targetValue ? "True" : "False";
              const percentageDiff = targetValue > 0 ? ((targetValue - yearlyWaterTotal) / targetValue * 100).toFixed(2) : "0.00";
          
              const theme = "Environment";
              const isTargetReached = targetReached === "True";
              themeCounts[theme].total += 1;
              if (isTargetReached) {
                themeCounts[theme].reached += 1;
              }
          
          
              rows.push(
                <tr key={`grouped-water-yearly`}>
                  <td>{srNo++}</td>
                  <td>Environment</td>
                  <td>Water withdrawal by source</td>
                  <td>Absolute</td>
                  <td>Kilo Litre</td>
                  <td>Yearly</td>
                  <td>-</td>
                  <td>{year}</td>
                  <td>{targetValue}</td>
                  <td>{yearlyWaterTotal.toFixed(2)}</td>
                  {/* <td>{selectedStatus}</td>
                  <td>{targetReached}</td> */}
                  <td>{percentageDiff}%</td>
                </tr>
              );
            }
            } else {
              Object.entries(scorecardData.groupedWater)
                .filter(([key]) => month === "All" || key.startsWith(month))
                .forEach(([key, total]) => {
          
                  if (total !== 0) {
                  const [monthPart, yearPart] = key.split("-");
                  const targetValue = parseFloat(calculateGroupedWaterTarget());
                  const selectedStatus = "Lagging"; // Always static
                  const targetReached = total <= targetValue ? "True" : "False";
                  const percentageDiff = targetValue > 0 ? (( targetValue - total) / targetValue * 100).toFixed(2) : "0.00";
          
          
                  const theme = "Environment";
                  const isTargetReached = targetReached === "True";
                  themeCounts[theme].total += 1;
                  if (isTargetReached) {
                    themeCounts[theme].reached += 1;
                  }
          
                  rows.push(
                    <tr key={`grouped-water-${key}`}>
                      <td>{srNo++}</td>
                      <td>Environment</td>
                      <td>Water withdrawal by source</td>
                      <td>Absolute</td>
                      <td>Kilo Litre</td>
                      <td>Monthly</td>
                      <td>{monthPart}</td>
                      <td>{yearPart}</td>
                      <td>{targetValue}</td>
                      <td>{total.toFixed(2)}</td>
                      {/* <td>{selectedStatus}</td>
                      <td>{targetReached}</td> */}
                      <td>{percentageDiff}%</td>
                    </tr>
                  );
                }
                });
            }
          }
          
          // Grouped Waste
          if (scorecardData.groupedWaste) {
            if (frequency === "Yearly") {
              const yearlyWasteTotal = Object.entries(scorecardData.groupedWaste)
                .filter(([key]) => key.endsWith(year)) 
                .reduce((sum, [, value]) => sum + value, 0); 
          
                if (yearlyWasteTotal !== 0) { 
              const targetValue = calculateGroupedWasteTarget();
              const targetPercentage = (targetValue / 100).toFixed(2); 
              const wastePercentage = targetValue > 0 ? ((yearlyWasteTotal / targetValue) * 100).toFixed(2) : "0.00"; 
              const isLeading = yearlyWasteTotal >= targetValue;
             const selectedStatus = "Leading"; 
             const percentageDiff = targetValue > 0 ? ((targetValue - yearlyWasteTotal) / targetValue * 100).toFixed(2) : "0.00";
          
             const theme = "Environment";
             const isTargetReached = selectedStatus === "Leading" && yearlyWasteTotal >= targetValue;
              themeCounts[theme].total += 1;
              if (isTargetReached) {
                themeCounts[theme].reached += 1;
              }
          
              rows.push(
                <tr key={`grouped-waste-yearly`}>
                  <td>{srNo}</td>
                  <td>Environment</td>
                  <td>Waste Diverted from Disposal</td>
                  <td>Absolute</td>
                  <td>%</td>
                  <td>Yearly</td>
                  <td>-</td>
                  <td>{year}</td>
                  <td>{targetPercentage}%</td> 
                  <td>{wastePercentage}%</td> 
                         {/* <td>{selectedStatus}</td>
                  <td>
                    {selectedStatus === "leading" && yearlyWasteTotal >= targetValue ? "True" :
                     selectedStatus === "lagging" && yearlyWasteTotal < targetValue ? "True" :
                     selectedStatus ? "False" : "-"}
                  </td> */}
                  <td>{percentageDiff}%</td>
                </tr>
              );
              srNo++;
            }
            } else {
          
              Object.entries(scorecardData.groupedWaste)
                .filter(([key]) => month === "All" || key.startsWith(month))
                .forEach(([key, total]) => {
          
                  if (total !== 0) {
                  const [monthPart, yearPart] = key.split("-");
                  const targetValue = calculateGroupedWasteTarget();
                  const targetPercentage = (targetValue / 100).toFixed(2); 
                  const wastePercentage = targetValue > 0 ? ((total / targetValue) * 100).toFixed(2) : "0.00"; 
                  const isLeading = total >= targetValue;
                 
                  const selectedStatus = "Leading"; 
                  const percentageDiff = targetValue > 0 ? (( targetValue - total) / targetValue * 100).toFixed(2) : "0.00";
          
                  const theme = "Environment";
                  const isTargetReached = selectedStatus === "Leading" && total >= targetValue;
                  themeCounts[theme].total += 1;
                  if (isTargetReached) {
                    themeCounts[theme].reached += 1;
                  }
          
          
                  rows.push(
                    <tr key={`grouped-waste-${key}`}>
                      <td>{srNo}</td>
                      <td>Environment</td>
                      <td>Waste Diverted from Disposal</td>
                      <td>Absolute</td>
                      <td>%</td>
                      <td>Monthly</td>
                      <td>{monthPart}</td>
                      <td>{yearPart}</td>
                      <td>{targetPercentage}%</td> 
                      <td>{wastePercentage}%</td> 
                      
                       {/* <td>{selectedStatus}</td>
                      <td>
                        {selectedStatus === "leading" && total >= targetValue ? "True" :
                         selectedStatus === "lagging" && total < targetValue ? "True" :
                         selectedStatus ? "False" : "-"}
                      </td> */}
                      <td>{percentageDiff}%</td>
                    </tr>
                  );
                  srNo++;
                }
                });
            }
          }
                    // Detailed Fields (water, waste, emission, energy)
                    ['water', 'waste', 'emission', 'energy'].forEach((dataset) => {
                      if (scorecardData[dataset]?.length > 0) {             
                        const filteredData = scorecardData[dataset]
                          .filter((item) => {
                            if (dataset === 'water' && item.fieldGroup === 'A') return false;
                            if (dataset === 'waste' && ['B1', 'B2', 'B3'].includes(item.fieldGroup)) return false;
                            if (frequency === 'Yearly') return item.frequencyYear === year;
                            if (frequency === 'Monthly') return month === "All" || item.frequencyMonth === month;
                            return true;
                           
                          })
                          .reduce((acc, item) => {
                            if (frequency === 'Yearly') {
                              const existing = acc.find((row) => row.fieldId === item.fieldId && row.fieldGroup === item.fieldGroup);
                              if (existing) {
                                existing.prevValue += item.prevValue || 0;
                              } else {
                                acc.push({ ...item, prevValue: item.prevValue || 0 });
                              }
                              return acc;
                            }
                            acc.push(item);
                            return acc;
                          }, []);
          
                        filteredData.forEach((item, idx) => {
                          const fieldType = item.fieldName?.toLowerCase().includes("intensity") ? "Intensity" : "Absolute";
                          const targetValue = targetValues[item.fieldId]
                            ? (frequency === "Monthly"
                                ? (targetValues[item.fieldId] / 12).toFixed(2)
                                : targetValues[item.fieldId])
                            : "-";
                          const currentValue = item.prevValue || 0;
                          //const selectedStatus = statusSelections[srNo];
                          const rawStatus = statusSelections[item.fieldId];
                          const selectedStatus = rawStatus === "isLeading" ? "leading" : rawStatus === "isLagging" ? "lagging" : "-";
          
          
                          const targetReached = selectedStatus === "leading" && currentValue >= targetValue ||
                          selectedStatus === "lagging" && currentValue < targetValue ? "True" : "False";
          
                        const theme = "Environment";
                        const isTargetReached = targetReached === "True";
                        themeCounts[theme].total += 1;
                        if (isTargetReached) themeCounts[theme].reached += 1;
                          
          
                          rows.push(
                            <tr key={`${dataset}-${idx}`}>
                              <td>{srNo}</td>
                              <td>Environment</td>
                              <td>{item.fieldName}</td>
                              <td>{fieldType}</td>
                              {/* <td>{item.unitName}</td> */}
                              <td>
            {item.fieldName === "Percentage of Renewable Energy" ? "%" : item.unitName}
          </td>
                              <td>{frequency}</td>
                              <td>{frequency === 'Yearly' ? '-' : item.frequencyMonth}</td>
                              <td>{item.frequencyYear}</td>
                              <td>{targetValue}</td>
                              <td>{currentValue.toFixed(2)}</td>
                             
                             {/* <td>
            {selectedStatus === "leading" ? "Leading" : selectedStatus === "lagging" ? "Lagging" : "-"}
          </td>
                     
                 <td>
            {selectedStatus === "leading" && currentValue >= targetValue ? "True" :
             selectedStatus === "lagging" && currentValue < targetValue ? "True" :
             "False"}
          </td> */}
          
          <td>
            {(() => {
              if (targetValue !== "-" && parseFloat(targetValue) > 0) {
                const diff = (( parseFloat(targetValue) - currentValue) / parseFloat(targetValue)) * 100;
                return `${diff.toFixed(2)}%`;
              }
              return "-";
            })()}
          </td>
                          </tr>
                          );
                          srNo++;
                        });
                      }
                    });
          
          
          // if (governanceData?.length > 0) {
          //   governanceData.forEach((item) => {
          //     const fieldId = item.fieldId;
          //     const fieldName = item.fieldName?.name || "";
          //     const fieldType = fieldName.toLowerCase().includes("intensity") ? "Intensity" : "Absolute";
          //     const unit = "-";
          //     const frequencyType = "Yearly";
          //     const currentValue = Number(item.value || 0);
          //     const targetValue = targetValues[fieldId] || "-";
          
          //     const rawStatus = statusSelections[fieldId];
          //     const selectedStatus =
          //       rawStatus === "isLeading"
          //         ? "leading"
          //         : rawStatus === "isLagging"
          //         ? "lagging"
          //         : "-";
          
          //     const targetReached =
          //       selectedStatus === "leading" && currentValue >= targetValue
          //         ? "True"
          //         : selectedStatus === "lagging" && currentValue < targetValue
          //         ? "True"
          //         : "False";
          
          //     const percentage =
          //       targetValue !== "-" && parseFloat(targetValue) > 0
          //         ? `${(((parseFloat(targetValue)-currentValue) / parseFloat(targetValue)) * 100).toFixed(2)}%`
          //         : "-";
          
          //         const theme = "Governance";
          //         const isTargetReached = targetReached === "True";
          
          //         themeCounts[theme].total += 1;
          //         if (isTargetReached) {
          //           themeCounts[theme].reached += 1;
          //         }
          
          
          //     rows.push(
          //       <tr key={`governance-${fieldId}`}>
          //         <td>{srNo}</td>
          //         <td>Governance</td>
          //         <td>{fieldName}</td>
          //         <td>{fieldType}</td>
          //         <td>No's</td>
          //         <td>{frequencyType}</td>
          //         <td>-</td>
          //         <td>{item.year}</td>
          //         <td>{targetValue}</td>
          //         <td>{currentValue}</td>
          //         {/* <td>{selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1)}</td>
          //         <td>{targetReached}</td> */}
          //         <td>{percentage}</td>
          //       </tr>
          //     );
          
          //     srNo++;
          //   });
          // }
          
          // if (socialData?.length > 0) {
          //   const femaleGroup = socialData.filter(item => item.fieldGroup === "A_B_B");
          //   const maleGroup = socialData.filter(item => item.fieldGroup === "A_B_A");
          
          //   const femaleTotal = femaleGroup.reduce((sum, item) => sum + Number(item.value || 0), 0);
          //   const maleTotal = maleGroup.reduce((sum, item) => sum + Number(item.value || 0), 0);
          //   const totalEmployees = femaleTotal + maleTotal;
          
          //   const femalePercentage = totalEmployees > 0 ? ((femaleTotal / totalEmployees) * 100).toFixed(2) : "0.00";
          //   const malePercentage = totalEmployees > 0 ? ((maleTotal / totalEmployees) * 100).toFixed(2) : "0.00";
          //   const targetValue = 50;
          
          //   const femaleStatus = parseFloat(femalePercentage) >= targetValue ? "Leading" : "Lagging";
          //   const maleStatus = parseFloat(malePercentage) >= targetValue ? "Leading" : "Lagging";
          
          //   const femaleTargetReached = femaleStatus === "Leading" ? "True" : "False";
          //   const maleTargetReached = maleStatus === "Leading" ? "True" : "False";
          
          //   const femaleDiff = (femalePercentage - targetValue).toFixed(2);
          //   const maleDiff = (malePercentage - targetValue).toFixed(2);
          
          //   themeCounts["Social"].total += 1;
          //   if (femaleTargetReached === "True") themeCounts["Social"].reached += 1;
          
          //   // Male row tracking
          //   themeCounts["Social"].total += 1;
          //   if (maleTargetReached === "True") themeCounts["Social"].reached += 1;
          
          
          //   // Female Workforce Diversity
          //   rows.push(
          //     <tr key={`female-diversity`}>
          //       <td>{srNo++}</td>
          //       <td>Social</td>
          //       <td>Female Workforce Diversity</td>
          //       <td>Absolute</td>
          //       <td>%</td>
          //       <td>Yearly</td>
          //       <td>-</td>
          //       <td>{year}</td>
          //       <td>{targetValue}%</td>
          //       <td>{femalePercentage}%</td>
          //       {/* <td>{femaleStatus}</td>
          //       <td>{femaleTargetReached}</td> */}
          //       <td>{femaleDiff}%</td>
          //     </tr>
          //   );
          
          //   // Male Workforce Diversity
          //   // rows.push(
          //   //   <tr key={`male-diversity`}>
          //   //     <td>{srNo++}</td>
          //   //     <td>Social</td>
          //   //     <td>Male Workforce Diversity</td>
          //   //     <td>Absolute</td>
          //   //     <td>%</td>
          //   //     <td>Yearly</td>
          //   //     <td>-</td>
          //   //     <td>{year}</td>
          //   //     <td>{targetValue}%</td>
          //   //     <td>{malePercentage}%</td>
          //   //     <td>{maleStatus}</td>
          //   //     <td>{maleTargetReached}</td>
          //   //     <td>{maleDiff}%</td>
          //   //   </tr>
          //   // );
          
          //   // Remaining (non-grouped) Social Fields
          //   socialData
          //     .filter(item => !["A_B_B", "A_B_A"].includes(item.fieldGroup))
          //     .forEach((item) => {
          //       const fieldId = item.fieldId;
          //       const rawFieldName = item.fieldName?.name?.trim() || "";
          //       const fieldName = rawFieldName;
          //       const fieldType = rawFieldName.toLowerCase().includes("intensity") ? "Intensity" : "Absolute";
          //       const unit = rawFieldName === "Average hours of training per employee" ? "Hours" : "No's";
          //       const frequencyType = item.months ? "Monthly" : "Yearly";
          //       const frequencyMonth = item.months || "-";
          //       const frequencyYear = item.year;
          //       const currentValue = Number(item.value || 0);
          //       const target = targetValues[fieldId];
          //       const targetValue = target !== undefined ? Number(target) : "-";
          
          //       const rawStatus = statusSelections[fieldId];
          //       const selectedStatus =
          //         rawStatus === "isLeading"
          //           ? "Leading"
          //           : rawStatus === "isLagging"
          //           ? "Lagging"
          //           : "-";
          
          //       const targetReached =
          //         selectedStatus === "Leading" && currentValue >= targetValue
          //           ? "True"
          //           : selectedStatus === "Lagging" && currentValue < targetValue
          //           ? "True"
          //           : "False";
          
          //       const percentage =
          //         targetValue !== "-" && parseFloat(targetValue) > 0
          //           ? `${(((parseFloat(targetValue) - currentValue) / parseFloat(targetValue)) * 100).toFixed(2)}%`
          //           : "-";
          
          //           const theme = "Social";
          //           const isTargetReached = targetReached === "True";
          //           themeCounts[theme].total += 1;
          //           if (isTargetReached) themeCounts[theme].reached += 1;
          
          //       rows.push(
          //         <tr key={`social-${fieldId}`}>
          //           <td>{srNo++}</td>
          //           <td>Social</td>
          //           <td>{fieldName}</td>
          //           <td>{fieldType}</td>
          //           <td>{unit}</td>
          //           <td>{frequencyType}</td>
          //           <td>{frequencyMonth}</td>
          //           <td>{frequencyYear}</td>
          //           <td>{targetValue}</td>
          //           <td>{currentValue}</td>
          //           {/* <td>{selectedStatus}</td>
          //           <td>{targetReached}</td> */}
          //           <td>{percentage}</td>
          //         </tr>
          //       );
          //     });
          // }
                    return rows;
                   
                  })()}
                </>
              )}
            </tbody>
          </Table>
          </div>
          
        </Card.Body>
      </Card>
    </Layout>
  );
};

export default observer(ScorecardExcelCompany3);
